import { useState, useEffect, useRef } from "react";

import { useCollision } from "./use-collision";
import { usePosition } from "./use-position";
import { useWorld } from "./use-world";
import { Sprite } from "./sprite";
import { useNavigate } from "./use-navigate";
import { usePathfinding } from "./use-pathfinding";

import { moveToTarget } from "./behaviours";

export const Player = ({
  x = 32,
  y = 32,
  width = 32,
  height = 32,
  config = {},
  ...rest
}) => {
  const cfg = {
    stride: 3,
    solid: console.log,
    center: true,
    ...config,
  };
  const [position, updatePosition] = usePosition(x, y, width, height, {
    ...cfg,
  });

  const [interactions, setInteractions] = useState([]);
  const [path, setPath] = useState([]);
  const pathIndexRef = useRef(0);
  const stuckCountRef = useRef(0);
  const [nav, updateNav] = useNavigate(position, cfg);
  const { addSprite, frame, screen, sprites, target, setTarget } = useWorld();
  const { isColliding, isInteracting } = useCollision(screen);
  const { findPath } = usePathfinding();

  const update = (newPosition) => {
    const collision = isColliding(newPosition, sprites);
    setInteractions(isInteracting(newPosition));
    if (target && target.x === position.x && target.y === position.y) {
      setTarget(null);
    }
    if (!collision) {
      stuckCountRef.current = 0;
      updateNav(newPosition);
      updatePosition(newPosition.x, newPosition.y, {
        direction: newPosition.direction,
      });
    } else {
      const { config } = collision;
      if (typeof config?.onCollide === "function") {
        config?.onCollide();
      }
      updateNav(position);
      stuckCountRef.current++;

      if (path.length > 0 && target) {
        if (stuckCountRef.current > 120) {
          setPath([]);
          setTarget(null);
          stuckCountRef.current = 0;
        } else if (stuckCountRef.current === 1 || stuckCountRef.current % 15 === 0) {
          const newPath = findPath(position, target, width, height);
          if (newPath.length > 1) {
            setPath(newPath);
            pathIndexRef.current = 1;
          } else if (newPath.length === 1) {
            setPath(newPath);
            pathIndexRef.current = 0;
          }
        }
      } else {
        setTarget(null);
      }
    }
  };

  useEffect(() => {
    update(nav);
  }, [nav]);

  useEffect(() => {
    if (interactions.length !== 0) {
      interactions.forEach((int) => {
        if (!int.interacting) {
          const { position } = int;
          position.interacting = true;
          position.dialog = [`hi, my name is ${position.id}`, "how are you?"];
          addSprite(position);
        }
      });
    }
  }, [interactions]);

  useEffect(() => {
    if (target) {
      const newPath = findPath(position, target, width, height);
      if (newPath.length > 1) {
        setPath(newPath);
        pathIndexRef.current = 1;
      } else {
        setPath([target]);
        pathIndexRef.current = 0;
      }
    }
  }, [target]);

  useEffect(() => {
    if (path.length > 0 && pathIndexRef.current < path.length) {
      const nextWaypoint = path[pathIndexRef.current];
      const distX = Math.abs(position.x - nextWaypoint.x);
      const distY = Math.abs(position.y - nextWaypoint.y);

      if (distX <= cfg.stride && distY <= cfg.stride) {
        pathIndexRef.current++;
        if (pathIndexRef.current >= path.length) {
          setPath([]);
          setTarget(null);
          return;
        }
      }

      const currentTarget = path[pathIndexRef.current];
      if (currentTarget) {
        update(moveToTarget(currentTarget.x, currentTarget.y, position, cfg.stride));
      }
    }
  }, [frame]);

  return <Sprite position={position} {...rest} />;
};
